import React from "react";
import { Helmet } from "react-helmet-async";
import { useStore } from "../context/StoreContext";
import { Icons } from "../components/Icons";
import ProductCard from "../components/ProductCard";
import CategoryTile, { catGridStyle } from "../components/CategoryTile";
import Skeleton from "../components/Skeleton";
import { shopPath } from "../utils/slug";
import "./Home.css";

const TICKER = [
  "American Diamond",
  "Zircon Collection",
  "Bridal Sets",
  "Jewellery Pakistan",
  "Est. 2001",
  "Free Delivery Rs.3000+",
  "Premium Quality",
  "Bridal Jewellery Sets",
];

/* Icon shown on a category tile that has no product photo yet.
   Purely presentational — the category names themselves always come
   from the database. */
const CATEGORY_ICONS = {
  rings: Icons.ring,
  necklaces: Icons.necklace,
  earrings: Icons.earring,
  bangles: Icons.bangle,
  bracelets: Icons.bracelet,
};

const BUDGET_CATS = [
  {
    key: "under1000",
    label: "Under Rs. 1,000",
    sub: "Gorgeous picks that won't break the bank",
    emoji: "✨",
    gradient: "linear-gradient(135deg,#1a1a2e 0%,#16213e 100%)",
    accent: "#D4AF37",
    filter: (p) => p.price < 1000,
  },
  {
    key: "under2000",
    label: "Under Rs. 2,000",
    sub: "Premium designs at amazing value",
    emoji: "💎",
    gradient: "linear-gradient(135deg,#2d1b33 0%,#1a1028 100%)",
    accent: "#E91E8C",
    filter: (p) => p.price >= 1000 && p.price < 2000,
  },
];

export default function Home() {
  const {
    products, productsLoading,
    catNames, catsLoading, categoryImage, catCount, subcatsFor,
    goShop, navigate, openWA,
    setShopCat, setShopSub, setShopSort, setShopPriceMin, setShopPriceMax,
  } = useStore();

  const openBudget = (key) => {
    setShopCat("All");
    setShopSub("All");
    setShopSort("pa");
    if (key === "under1000") {
      setShopPriceMin(null);
      setShopPriceMax(1000);
    } else if (key === "under2000") {
      setShopPriceMin(1000);
      setShopPriceMax(2000);
    }
    navigate("shop");
  };

  const iconFor = (name) => CATEGORY_ICONS[String(name).toLowerCase()] || Icons.diamond;

  const showCatSkeleton = catsLoading && catNames.length === 0;
  const showProductSkeleton = productsLoading && products.length === 0;

  return (
    <div className="home-page">
      <Helmet>
        <title>Artificial Jewellery Online in Pakistan | City Shopping Center</title>
        <meta
          name="description"
          content="Buy artificial jewellery online in Pakistan — American Diamond, zircon &amp; bridal jewellery sets. Rings, necklaces, earrings, bangles. Free delivery over Rs. 3,000. Est. 2001, Daska."
        />
        <link rel="canonical" href="https://cityshopingcenter.com/" />
      </Helmet>

      {/* ── HERO ─────────────────────────────────────── */}
      <section className="hero">
        <div className="hero-orb hero-orb--1" />
        <div className="hero-orb hero-orb--2" />
        <div className="hero-orb hero-orb--3" />

        <div className="hero-inner">
          <div className="hero-left" data-reveal="left">
            <div className="hero-badge">
              <span className="hero-badge-dot" />
              Jewellery Shop in Daska, Sialkot
            </div>

            <h1 className="hero-title">
              <em>JEWELLERY</em>FOR HER
            </h1>

            <p className="hero-sub">
              American Diamond, zircon &amp; bridal jewellery sets —<br />
              delivered across Pakistan in 2&ndash;4 days.
            </p>

            <div className="hero-actions">
              <button className="btn-primary hero-btn-main" onClick={() => goShop("All")}>
                {Icons.diamond(16)} Shop Collection
              </button>
              <button className="btn-secondary" onClick={() => navigate("about")}>
                Our Story
              </button>
            </div>

            <div className="hero-trust">
              <span className="trust-pill">{Icons.check(12)} Quality Checked</span>
              <span className="trust-pill">{Icons.clock(12)} 2–4 Day Delivery</span>
              <span className="trust-pill">{Icons.star(12)} Est. 2001</span>
            </div>
          </div>

          <div className="hero-right" data-reveal="right">
            <div className="hero-stat-card hero-card-glow">
              <div className="hero-stat-icon">{Icons.diamond(26)}</div>
              <div className="hero-stat-title">American Diamond</div>
              <div className="hero-stat-label">Premium sparkle</div>
            </div>

            <div className="hero-stat-card">
              <div className="hero-stat-icon">{Icons.check(24)}</div>
              <div className="hero-stat-title">Quality Checked</div>
              <div className="hero-stat-label">Before every dispatch</div>
            </div>

            <div className="hero-stat-card">
              <div className="hero-stat-icon">{Icons.clock(24)}</div>
              <div className="hero-stat-title">Fast Delivery</div>
              <div className="hero-stat-label">2–4 days nationwide</div>
            </div>

            <div className="hero-stat-card hero-info-card">
              <div className="hero-info-title">Est. 2001</div>
              <div className="hero-info-sub">Two decades of trusted craftsmanship</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TICKER ──────────────────────────────────── */}
      <div className="ticker-wrap">
        <div className="ticker-track">
          {[...TICKER, ...TICKER].map((t, i) => (
            <div key={i} className="ticker-item">{t}</div>
          ))}
        </div>
      </div>

      {/* ── CATEGORIES ──────────────────────────────── */}
      {(showCatSkeleton || catNames.length > 0) && (
        <section className="section-wrapper">
          <div className="section-header center" data-reveal="up">
            <span className="section-tag">Browse by Category</span>
            <h2 className="section-title">OUR COLLECTIONS</h2>
          </div>

          {showCatSkeleton ? (
            <Skeleton.CategoryTiles count={5} />
          ) : (
            <div className="cat-grid" style={catGridStyle(catNames.length)}>
              {catNames.map((name, ci) => {
                const subs = subcatsFor(name);
                const n = catCount(name);
                return (
                  <div key={name} data-reveal="up" style={{ transitionDelay: (ci * 0.06) + "s" }}>
                    <CategoryTile
                      name={name}
                      image={categoryImage(name)}
                      icon={iconFor(name)}
                      meta={
                        subs.length
                          ? `${subs.length} ${subs.length === 1 ? "type" : "types"}`
                          : n > 0
                            ? `${n} ${n === 1 ? "piece" : "pieces"}`
                            : undefined
                      }
                      href={shopPath(name)}
                      onClick={() => goShop(name)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ── BUDGET CATEGORIES ───────────────────────── */}
      <section className="section-wrapper budget-section">
        <div className="section-header center" data-reveal="up">
          <span className="section-tag">Smart Shopping</span>
          <h2 className="section-title">SHOP BY BUDGET</h2>
          <p className="section-sub">Beautiful jewellery for every budget</p>
        </div>

        <div className="budget-grid">
          {BUDGET_CATS.map((bc, bi) => {
            const count = products.filter(bc.filter).length;
            return (
              <button
                key={bc.key}
                type="button"
                className="budget-card"
                data-reveal="zoom"
                style={{ background: bc.gradient, transitionDelay: (bi * 0.1) + "s" }}
                onClick={() => openBudget(bc.key)}
              >
                <span className="budget-card-emoji">{bc.emoji}</span>
                <span className="budget-card-label" style={{ color: bc.accent }}>
                  {bc.label}
                </span>
                <span className="budget-card-sub">{bc.sub}</span>
                {count > 0 && (
                  <span className="budget-card-count" style={{ borderColor: bc.accent + "44", color: bc.accent }}>
                    {count} {count === 1 ? "item" : "items"} available
                  </span>
                )}
                <span className="budget-card-cta" style={{ color: bc.accent }}>
                  Shop Now {Icons.arrow(16)}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── FEATURED ────────────────────────────────── */}
      <section className="featured-section">
        <div className="section-wrapper">
          <div className="section-header center" data-reveal="up">
            <span className="section-tag">Handpicked for You</span>
            <h2 className="section-title">FEATURED PIECES</h2>
          </div>

          {showProductSkeleton ? (
            <Skeleton.ProductGrid count={4} />
          ) : (
            <div className="products-grid">
              {products.length === 0 ? (
                <div className="empty-state">
                  {Icons.diamond(48)}
                  <p>Jewellery products will appear here once added from the Admin Panel</p>
                </div>
              ) : (
                products.slice(0, 4).map((p, pi) => (
                  <div key={p.id} data-reveal="up" style={{ transitionDelay: ((pi % 4) * 0.08) + "s" }}>
                    <ProductCard product={p} priority={pi < 2} />
                  </div>
                ))
              )}
            </div>
          )}

          {products.length > 4 && (
            <div className="featured-more">
              <button className="btn-primary" onClick={() => goShop("All")}>
                View All Jewellery
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────── */}
      <div className="cta-band" data-reveal="zoom">
        <h2>CHAT WITH US</h2>
        <p>Get personalised jewellery recommendations on WhatsApp</p>
        <button className="btn-cta" onClick={openWA}>
          {Icons.whatsapp(18)} Chat on WhatsApp
        </button>
      </div>
    </div>
  );
}
